/**
 * Teamspace chooser — the STANDALONE top-bar shell, rendered in the notification bell's old slot
 * beside the user menu (spec `26-spec-topnav-0826.md` §3, T981).
 * Gated by `TOP_NAV_TEAM_SWITCHER` at the call site.
 *
 * Same list as the profile-menu sub-menu row (`TeamSwitcher`), different shell: Radix sub-menu
 * parts can't live outside a parent `DropdownMenu`, so a top-bar control needs its own trigger +
 * `DropdownMenu`. Everything else — the [ALL] entry, its gating, the ROOT relabel, the one-team
 * guard — comes from `TeamScopeList`.
 *
 * `compact` is the responsive rung: label + chevron normally, icon-only when the nav is cramped.
 * It NEVER hides — losing the scope indicator would make the [ALL] scope invisible.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ChevronDown, Settings, Users } from 'lucide-react';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import { useTeamNoun } from '@/hooks/useTeamNoun';
import { useUserManagementAccess } from './useUserManagementAccess';
import { selectIsAllTeamsScope, useTeamStore } from '@/stores/team-store';
import { TeamspaceIcon } from '@/features/workspaces/components/TeamspaceIcon';
import { TeamScopeList, useCanSwitchTeamScope, useTeamScopeLabel } from './TeamScopeList';

/**
 * The Manage footer's two shortcuts, side by side (Jessica, 2026-10-01): Users, and Settings →
 * the Teamspaces page, whose settings nav reaches Roles and Groups. Moved here from the profile
 * menu (`UserMenu`), which drops its user-management items whenever this menu shows; gates in
 * `useUserManagementAccess`.
 */
const MANAGE_LINKS = [
  {
    key: 'users',
    label: 'Users',
    path: '/app/s/users',
    icon: Users,
    testId: 'team-switcher-link-users',
  },
  {
    key: 'teamspaces',
    label: 'Settings',
    path: '/app/s/teamspaces',
    icon: Settings,
    testId: 'team-switcher-link-settings',
  },
] as const;

interface TeamScopeButtonProps {
  /** Icon-only trigger (no label, no chevron) — the narrow-nav state. */
  compact?: boolean;
  className?: string;
}

export function TeamScopeButton({ compact, className }: TeamScopeButtonProps) {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  const canSwitchTeamScope = useCanSwitchTeamScope();
  const scopeLabel = useTeamScopeLabel();
  const isAllTeamsScope = useTeamStore(selectIsAllTeamsScope);
  const activeTeam = useTeamStore((s) => s.teams.find((team) => team._id === s.activeTeamId));

  const userManagementAccess = useUserManagementAccess();
  const visibleLinks = MANAGE_LINKS.filter((link) => userManagementAccess[link.key]);
  const hasManageSection = visibleLinks.length > 0;

  if (!canSwitchTeamScope) return null;

  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size={compact ? 'icon-md' : 'md'}
        // Same height + radius as the search field beside it, but outline-only (no fill) so it
        // reads as "pick a scope", not a second "type here" field.
        className={cn(
          'shrink-0 cursor-pointer rounded-lg border border-muted-foreground/20 bg-transparent text-foreground transition-colors hover:border-muted-foreground/40 hover:bg-transparent data-[state=open]:border-muted-foreground/40',
          !compact && 'gap-2 px-3',
          className,
        )}
        // Icon-only state has no visible text; the labeled state must NOT be overridden by an
        // aria-label (WAI-ARIA: prefer the visible label).
        aria-label={compact ? t('Switch {{noun}}', { noun }) : undefined}
        data-testid="team-switcher-trigger"
      >
        {/* The active teamspace's tile; the generic people glyph stays for the [ALL] scope. */}
        {isAllTeamsScope || !activeTeam ? (
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <TeamspaceIcon
            name={scopeLabel}
            icon={activeTeam.icon}
            color={activeTeam.iconColor}
            size="sm"
            // The tile's default bg-muted matches the nav, so step it darker to stay visible.
            className="bg-sidebar-active"
          />
        )}
        {!compact && (
          <>
            <span className="min-w-0 max-w-[140px] truncate text-sm font-normal">{scopeLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </>
        )}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {compact ? (
        // Icon-only: the current scope has nowhere to show, so the tooltip carries it.
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="bottom">{scopeLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="w-72 overflow-x-hidden p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <TeamScopeList />
        {/* Manage links at the BOTTOM as side-by-side buttons, under a rule — no section headers
            (Jessica, 2026-10-02). */}
        {hasManageSection && visibleLinks.length > 0 && (
          <>
            {/* my-0: the default top margin left a white strip between the scrolling list and
                the rule, so rows looked cut off above it instead of sliding under it. */}
            <DropdownMenuSeparator className="mx-0 my-0" />
            <div
              className={cn(
                'grid gap-2 p-2',
                visibleLinks.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
              )}
            >
              {visibleLinks.map(({ label, path, icon: Icon, testId }) => (
                <DropdownMenuItem
                  key={path}
                  asChild
                  className="cursor-pointer justify-center gap-2 border"
                >
                  <Link to={path} data-testid={testId}>
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    {t(label)}
                  </Link>
                </DropdownMenuItem>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
